/**
 * useProperties Hook
 * React Query hook for fetching properties
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { propertiesApi, Property, PropertyDetails, PropertiesListParams } from '@/features/properties/api';
import type { PaginatedResponse } from '@/shared/api/types';

const MAX_DETAIL_RETRIES = 3;
const HTTP_CLIENT_ERROR_MIN = 400;
const HTTP_SERVER_ERROR_MIN = 500;

/** A 4xx: the request itself is the problem, so repeating it cannot help. */
function isClientError(error: unknown): boolean {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    return status !== undefined && status >= HTTP_CLIENT_ERROR_MIN && status < HTTP_SERVER_ERROR_MIN;
}

// Query keys for consistent caching
export const PROPERTY_QUERY_KEYS = {
    all: ['properties'] as const,
    lists: () => [...PROPERTY_QUERY_KEYS.all, 'list'] as const,
    list: (filters?: PropertiesListParams) => [...PROPERTY_QUERY_KEYS.lists(), filters] as const,
    publicList: (filters?: PropertiesListParams) => [...PROPERTY_QUERY_KEYS.all, 'public', filters] as const,
    detail: (id: string) => [...PROPERTY_QUERY_KEYS.all, 'detail', id] as const,
};

interface UsePropertiesOptions {
    /** Use public API (no auth required) - defaults to false */
    isPublic?: boolean;
}

/**
 * Hook for fetching properties with optional filters
 * @param filters - Query parameters for filtering
 * @param hookOptions - Hook configuration (isPublic)
 * @param queryOptions - React Query options
 */
export const useProperties = (
    filters?: PropertiesListParams,
    hookOptions?: UsePropertiesOptions,
    queryOptions?: Omit<UseQueryOptions<PaginatedResponse<Property>, Error>, 'queryKey' | 'queryFn'>
) => {
    const isPublic = hookOptions?.isPublic ?? false;

    return useQuery({
        queryKey: isPublic
            ? PROPERTY_QUERY_KEYS.publicList(filters)
            : PROPERTY_QUERY_KEYS.list(filters),
        queryFn: () => isPublic
            ? propertiesApi.getForSale(filters)
            : propertiesApi.getProperties(filters),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        ...queryOptions,
    });
};

/**
 * Hook for fetching public properties for sale (convenience wrapper)
 * @param filters - Query parameters for filtering
 * @param queryOptions - React Query options
 */
export const usePublicProperties = (
    filters?: PropertiesListParams,
    queryOptions?: Omit<UseQueryOptions<PaginatedResponse<Property>, Error>, 'queryKey' | 'queryFn'>
) => {
    return useProperties(filters, { isPublic: true }, queryOptions);
};

/**
 * Hook for fetching property details by listing ID
 */
export const usePropertyDetails = (
    listingId: string,
    options?: Omit<UseQueryOptions<PropertyDetails, Error>, 'queryKey' | 'queryFn'>
) => {
    return useQuery({
        queryKey: [...PROPERTY_QUERY_KEYS.detail(listingId), 'details'] as const,
        queryFn: () => propertiesApi.getPropertyDetails(listingId),
        enabled: !!listingId,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        // Retry transient faults (5xx, network, timeout) but never a 4xx:
        // a listing that genuinely isn't there will not appear on the
        // fourth ask, and retrying it just makes the visitor wait through
        // four round trips before the page can say so.
        retry: (failureCount, error) => !isClientError(error) && failureCount < MAX_DETAIL_RETRIES,
        ...options,
    });
};

export default useProperties;
